import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, History } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { recentApi } from "@/api/ux";
import { Card } from "@/design-system/components/ui";
import { relativeTime } from "@/components/ux/relativeTime";
import {
  entityTypeLabel,
  isCapitalMarketsEntity,
  resolveCapitalMarketsRoute,
} from "./cmEntities";

/**
 * SPV Pool v1.0 §4.7 / PR 9 — Recently Viewed widget scoped to the Capital
 * Markets module. Reads the unified recently-viewed feed (populated by the
 * `RecordView` action filter on SPV / Pool / Investor / service-provider
 * detail GETs) and shows the last ~10 Capital Markets entities.
 */
export function CapitalMarketsRecentlyViewed() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const recent = useQuery({
    queryKey: ["recent"],
    queryFn: () => recentApi.list(),
  });

  // Detail GETs record server-side; invalidate on route change so the widget
  // reflects the latest view after navigating back to the dashboard.
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["recent"] });
  }, [location.pathname, queryClient]);

  const items = (recent.data ?? [])
    .filter((r) => isCapitalMarketsEntity(r.entityType))
    .slice(0, 10);

  return (
    <Card>
      <div className="flex items-center gap-space-2 mb-space-3">
        <History
          size={16}
          strokeWidth={1.5}
          className="text-gold-shadow"
          aria-hidden
        />
        <h2 className="font-display font-bold text-h3 m-0">Recently Viewed</h2>
      </div>

      {recent.isLoading && (
        <p className="text-small text-gray-mid italic m-0">Loading…</p>
      )}
      {!recent.isLoading && items.length === 0 && (
        <p
          className="text-small text-gray-mid m-0"
          data-testid="recently-viewed-empty"
        >
          Open a pool, trust, investor, or service provider and it will appear
          here.
        </p>
      )}

      <ul className="m-0 p-0 list-none flex flex-col">
        {items.map((item) => (
          <li
            key={`${item.entityType}-${item.entityId}`}
            className="border-t border-border-light first:border-t-0"
          >
            <Link
              to={resolveCapitalMarketsRoute(item.entityType, item.entityId)}
              className="flex items-center justify-between gap-space-2 py-space-2 text-small text-onyx hover:bg-gold-highlight/10 rounded-sm px-space-2 -mx-space-2"
            >
              <span className="flex flex-col min-w-0">
                <span className="truncate">{item.entityTitle}</span>
                <span className="text-label text-gray-mid uppercase tracking-label">
                  {entityTypeLabel(item.entityType)}
                </span>
              </span>
              <span className="flex items-center gap-space-2 shrink-0">
                <span className="text-label text-gray-mid">
                  {relativeTime(item.viewedAt)}
                </span>
                <ChevronRight
                  size={14}
                  strokeWidth={1.5}
                  className="text-gray-mid"
                  aria-hidden
                />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
